/*
  One lesson, full screen. This is where a tapped alarm or notification lands (/lesson/:id), so it
  leads with the big Join button. Also reachable from the timetable.
*/
import { ArrowLeftOutlined, CopyOutlined, PlayCircleOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { message } from "antd";
import Badge from "app/components/Badge";
import UpcomingLessonCard from "app/components/Cards/UpcomingLessonCard";
import { studentNamesFor } from "app/components/LessonSheet";
import Spinner, { EmptyState } from "app/components/Spinner";
import { Button } from "app/components/v1/Button";
import { openExternal } from "app/native/nativeApp";
import { useGetLessonQuery } from "app/services/timetable";
import { lessonPhase } from "app/utils/time";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function LessonPage() {
  const { registerId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetLessonQuery(Number(registerId), { skip: !registerId, pollingInterval: 60000 });

  return (
    <div className="ct-page">
      <div className="d-flex align-items-center gap-2 mb-3" style={{ paddingTop: "var(--ct-safe-top)" }}>
        <Button buttonType="text" color="secondary" icon={<ArrowLeftOutlined />} aria-label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} />
        <h1 className="m-0" style={{ fontSize: 22 }}>Lesson</h1>
      </div>
      {isLoading ? <Spinner /> : isError || !data ? (
        <EmptyState title="We could not find that lesson" hint="It may have been removed from the timetable." />
      ) : (
        <>
          <UpcomingLessonCard lesson={data.lesson} studentNames={studentNamesFor(data.lesson, data.children)} label={lessonPhase(data.lesson.start, data.lesson.end) === "live" ? "Lesson in progress" : "Your lesson"} />
          <div className="ct-card p-3 mt-3">
            <div className="ct-subtitle mb-2">Details</div>
            <div className="d-flex flex-wrap gap-2">
              <Badge status="primary" text={data.lesson.classId} capitalize={false} />
              {data.lesson.teacher && <Badge status="muted" text={`Teacher ${data.lesson.teacher}`} capitalize={false} />}
              {data.lesson.classType && <Badge status="muted" text={data.lesson.classType} />}
            </div>
            {data.lesson.zoomMeetingId && !data.lesson.cancelled && (
              <div className="d-flex gap-2 mt-3">
                <Button className="flex-grow-1" buttonType="outlined" color="secondary" size="sm" icon={<CopyOutlined />} name={`ID ${data.lesson.zoomMeetingId}`} onClick={() => navigator.clipboard?.writeText(data.lesson.zoomMeetingId!).then(() => message.success("Meeting ID copied"))} />
                {data.lesson.zoomMeetingPasscode && <Button className="flex-grow-1" buttonType="outlined" color="secondary" size="sm" icon={<CopyOutlined />} style={{ textTransform: "none" }} name={`Passcode ${data.lesson.zoomMeetingPasscode}`} onClick={() => navigator.clipboard?.writeText(data.lesson.zoomMeetingPasscode!).then(() => message.success("Passcode copied"))} />}
              </div>
            )}
            {data.lesson.zoomLink && !data.lesson.cancelled && lessonPhase(data.lesson.start, data.lesson.end) === "past" && data.lesson.recordingLink && (
              <Button className="w-100 mt-3" icon={<PlayCircleOutlined />} name="Watch the recording" onClick={() => openExternal(data.lesson.recordingLink!)} />
            )}
            {data.lesson.zoomLink && !data.lesson.cancelled && lessonPhase(data.lesson.start, data.lesson.end) !== "past" && (
              <Button className="w-100 mt-3" size="lg" icon={<VideoCameraOutlined />} name="Join on Zoom" onClick={() => openExternal(data.lesson.zoomLink!)} />
            )}
          </div>
          <p className="ct-muted mt-3" style={{ fontSize: 13 }}>Problem joining? <Link to="/messages">Message the team</Link> and we will help straight away.</p>
        </>
      )}
    </div>
  );
}
